import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow, format } from "date-fns";
import { trpc } from "../../lib/api/trpc";

type QueryLog = {
  conversationId: string;
  messageId: string;
  query: string;
  answer: string;
  fullAnswer: string;
  confidence: string | null;
  citationCount: number;
  citations: Array<Record<string, unknown>>;
  source: string;
  askedAt: string;
  userId: string;
};

type QueryLogsResponse = {
  logs: QueryLog[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

function ConfidenceBadge({ level }: { level: string | null }) {
  const styles: Record<string, string> = {
    high: "bg-[var(--ok-bg)] text-[var(--ok-text)]",
    medium: "bg-[var(--accent-tint)] text-[var(--accent)]",
    low: "bg-[var(--warn-bg)] text-[var(--warn-text)]",
    insufficient: "bg-[var(--err-bg)] text-[var(--err-text)]",
  };

  if (!level)
    return <span className="text-[11.5px] text-[var(--text-faint)]">-</span>;

  return (
    <span
      className={`inline-flex items-center text-[11.5px] font-medium px-[9px] py-[3px] rounded-[7px] ${styles[level] || styles.low}`}
    >
      {level}
    </span>
  );
}

function SourceBadge({ source }: { source: string }) {
  const labels: Record<string, string> = {
    TEAMS_BOT: "Teams",
    ADMIN_CHAT: "Admin",
    API: "API",
  };

  return (
    <span className="text-[12.5px] text-[var(--text-3)]">
      {labels[source] || source}
    </span>
  );
}

const confidenceDescriptions: Record<string, string> = {
  high: "Answered confidently from indexed documents",
  medium: "Answered but with some uncertainty",
  low: "Answer found but confidence is low \u2014 review recommended",
  insufficient: "Could not answer from available documents",
};

function ExpandedRow({ log }: { log: QueryLog }) {
  const [showFull, setShowFull] = useState(false);
  const answerPreview =
    log.fullAnswer.length > 600
      ? log.fullAnswer.slice(0, 600) + "\u2026"
      : log.fullAnswer;

  return (
    <div className="px-4 py-4 bg-[var(--surface-2)] space-y-3">
      <div>
        <p className="text-xs font-medium text-[var(--text-faint)] mb-1">
          Full Query
        </p>
        <p className="text-sm text-[var(--text-2)]">{log.query}</p>
      </div>
      <div>
        <p className="text-xs font-medium text-[var(--text-faint)] mb-1">
          Answer
        </p>
        <p className="text-sm text-[var(--text-2)] whitespace-pre-wrap">
          {showFull ? log.fullAnswer : answerPreview}
        </p>
        {log.fullAnswer.length > 600 && (
          <button
            onClick={() => setShowFull(!showFull)}
            className="text-[var(--accent-strong)] font-medium hover:underline text-xs mt-1"
          >
            {showFull ? "Show less" : "Show full"}
          </button>
        )}
      </div>
      {log.confidence && (
        <div>
          <p className="text-xs font-medium text-[var(--text-faint)] mb-1">
            Confidence
          </p>
          <p className="text-sm text-[var(--text-2)]">
            <ConfidenceBadge level={log.confidence} />{" "}
            <span className="ml-2">
              {confidenceDescriptions[log.confidence] || ""}
            </span>
          </p>
        </div>
      )}
      {log.citations.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-faint)] mb-1">
            Citations ({log.citations.length})
          </p>
          <ul className="text-sm text-[var(--text-2)] space-y-1">
            {log.citations.map((cit, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-xs text-[var(--text-faint)] flex-shrink-0">
                  [{cit.citationNumber as number}]
                </span>
                <span>
                  {(cit.relevantText as string)
                    ? (cit.relevantText as string).length > 120
                      ? (cit.relevantText as string).slice(0, 120) + "\u2026"
                      : (cit.relevantText as string)
                    : `Chunk ${(cit.chunkId as string)?.slice(-8) || "unknown"}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function QueryLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const page = parseInt(searchParams.get("page") || "0", 10);
  const confidence = (searchParams.get("confidence") || "all") as string;
  const source = (searchParams.get("source") || "all") as string;
  const dateFrom = searchParams.get("dateFrom") || undefined;
  const dateTo = searchParams.get("dateTo") || undefined;
  const search = searchParams.get("search") || undefined;

  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data, isLoading, isError } = trpc.analytics.getQueryLogs.useQuery({
    page,
    pageSize: 25,
    search,
    confidence,
    source,
    dateFrom,
    dateTo,
  });

  const result = data as QueryLogsResponse | undefined;

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = Object.fromEntries(searchParams.entries());
      if (searchInput) {
        params.search = searchInput;
      } else {
        delete params.search;
      }
      params.page = "0";
      setSearchParams(params);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const updateFilter = useCallback(
    (key: string, value: string) => {
      const params = Object.fromEntries(searchParams.entries());
      if (value === "all" || value === "") {
        delete params[key];
      } else {
        params[key] = value;
      }
      params.page = "0";
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      const params = Object.fromEntries(searchParams.entries());
      params.page = String(p);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasFilters =
    search || confidence !== "all" || source !== "all" || dateFrom || dateTo;

  return (
    <div className="space-y-[18px]">
      {/* Filter bar */}
      <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search queries..."
            className="flex-1 min-w-[200px] h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)] focus:ring-accent focus:border-accent"
          />
          <select
            value={confidence}
            onChange={(e) => updateFilter("confidence", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          >
            <option value="all">All confidence</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="insufficient">Unanswered</option>
          </select>
          <select
            value={source}
            onChange={(e) => updateFilter("source", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          >
            <option value="all">All sources</option>
            <option value="TEAMS_BOT">Teams Bot</option>
            <option value="ADMIN_CHAT">Admin Chat</option>
            <option value="API">API</option>
          </select>
          <input
            type="date"
            value={dateFrom || ""}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          />
          <input
            type="date"
            value={dateTo || ""}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
            className="h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] text-[var(--text-2)]"
          />
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-8 animate-pulse">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 bg-[var(--surface-2)] rounded-[9px]"
              />
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="bg-[var(--err-bg)] border border-[var(--err-bg)] rounded-[12px] p-4">
          <p className="text-sm text-[var(--err-text)]">
            Failed to load query logs.
          </p>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {result.logs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[var(--text-3)]">
                {hasFilters
                  ? "No queries match your filters."
                  : "No queries yet."}
              </p>
            </div>
          ) : (
            <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-[var(--surface-2)] border-b border-[var(--border-2)]">
                  <tr>
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px] w-8" />
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                      Asked
                    </th>
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                      Query
                    </th>
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                      Confidence
                    </th>
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                      Citations
                    </th>
                    <th className="text-left text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-faint)] px-5 py-[11px]">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {result.logs.map((log) => {
                    const isExpanded = expandedRows.has(log.conversationId);
                    return (
                      <tr
                        key={log.conversationId}
                        className="group border-b border-[var(--border-2)]"
                      >
                        <td colSpan={6} className="p-0">
                          <div
                            onClick={() => toggleRow(log.conversationId)}
                            className="flex items-center cursor-pointer hover:bg-[var(--surface-2)] transition-colors"
                          >
                            <div className="px-5 py-3 w-8">
                              {isExpanded ? (
                                <ChevronUpIcon className="h-4 w-4 text-[var(--text-faint)]" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-[var(--text-faint)]" />
                              )}
                            </div>
                            <div
                              className="px-5 py-3 whitespace-nowrap text-[13px] text-[var(--text-3)]"
                              title={format(
                                new Date(log.askedAt),
                                "MMM d, yyyy h:mm a",
                              )}
                            >
                              {formatDistanceToNow(new Date(log.askedAt), {
                                addSuffix: true,
                              })}
                            </div>
                            <div className="px-5 py-3 flex-1 min-w-0">
                              <span className="text-[13px] text-[var(--text)] truncate block max-w-md">
                                {log.query.length > 80
                                  ? log.query.slice(0, 80) + "\u2026"
                                  : log.query}
                              </span>
                            </div>
                            <div className="px-5 py-3 whitespace-nowrap">
                              <ConfidenceBadge level={log.confidence} />
                            </div>
                            <div className="px-5 py-3 whitespace-nowrap text-[13px] text-[var(--text-3)]">
                              {log.citationCount > 0
                                ? `${log.citationCount} source${log.citationCount !== 1 ? "s" : ""}`
                                : "-"}
                            </div>
                            <div className="px-5 py-3 whitespace-nowrap">
                              <SourceBadge source={log.source} />
                            </div>
                          </div>
                          {isExpanded && <ExpandedRow log={log} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {result.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-2)]">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page <= 0}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="text-[13px] text-[var(--text-3)]">
                    Page {page + 1} of {result.pagination.totalPages} (
                    {result.pagination.total} total)
                  </span>
                  <button
                    onClick={() =>
                      setPage(
                        Math.min(result.pagination.totalPages - 1, page + 1),
                      )
                    }
                    disabled={page >= result.pagination.totalPages - 1}
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[13px] font-medium text-[var(--text-2)] hover:bg-[var(--surface-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
